import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { trackEvent } from "../utils/analytics";

const KEY = "ninas_lang_v2"; // bumped to reset old 'both' values
const VALID = ["en", "es"];
const LangContext = createContext({ lang: "en", setLang: () => {} });

function detectBrowserLang() {
  if (typeof navigator === "undefined") return "en";
  const nav = (navigator.language || navigator.languages?.[0] || "en").toLowerCase();
  return nav.startsWith("es") ? "es" : "en";
}

function readInitialLang() {
  if (typeof window === "undefined") return "en";
  // 1) URL wins for shareable links (?lang=es | ?lang=en)
  try {
    const q = new URL(window.location.href).searchParams.get("lang");
    if (q && VALID.includes(q)) return q;
  } catch (e) { /* ignore */ }
  // 2) localStorage
  try {
    const stored = localStorage.getItem(KEY);
    if (stored && VALID.includes(stored)) return stored;
  } catch (e) { /* ignore */ }
  // 3) Browser/OS language — Spanish speakers see Spanish, everyone else English (default)
  return detectBrowserLang();
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => (typeof window === "undefined" ? "en" : readInitialLang()));
  const prevLang = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try { document.documentElement.setAttribute("lang", lang); } catch (e) { /* ignore */ }
    try {
      const url = new URL(window.location.href);
      const current = url.searchParams.get("lang");
      // EN is default → keep URL clean by removing the param
      if (lang === "en") {
        if (current !== null) {
          url.searchParams.delete("lang");
          window.history.replaceState({}, "", url.toString());
        }
      } else if (current !== lang) {
        url.searchParams.set("lang", lang);
        window.history.replaceState({}, "", url.toString());
      }
    } catch (e) { /* ignore */ }
    // Only push a language_change event when the value actually changes
    // (skip initial mount + StrictMode double-invoke) to avoid inflating GA4.
    if (prevLang.current !== null && prevLang.current !== lang) {
      trackEvent("language_change", { language: lang });
    }
    prevLang.current = lang;
  }, [lang]);

  useEffect(() => {
    const onPop = () => setLangState(readInitialLang());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const setLang = useCallback((v) => {
    if (!VALID.includes(v)) return;
    setLangState(v);
    try { localStorage.setItem(KEY, v); } catch (e) { /* ignore */ }
  }, []);

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}

// Convenience: pick(en, es) returns the right variant.
export function useT() {
  const { lang } = useLang();
  return (en, es) => (lang === "es" ? es : en);
}
